import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AppCard,
  AppDeleteModal,
  AppInput,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { axiosPath } from '../../../routing/helpers/router';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import colors from '../../../constants/colors';
import DocumentManagerSidebar from '../../../layout/screenSpecifics/Modules/DocumentManager/Sidebar/DocumentManagerSidebar';
import * as moduleAction from '../../../store/actions/moduleAction';
import ColorPicker from '@/layout/components/form/ColorPicker';

export default function ModuleDocumentManagerTagListScreen() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const loading = useSelector((state) => state.currentPage?.loading);
  const tags = useSelector((state) => state.currentPage?.tags);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [validationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required('Introduce un nombre'),
      color: Yup.number().required(),
    }),
  );

  useEffect(() => {
    dispatch(currentPageAction.change('loading', true));
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD TAGS
  const loadTags = async () =>
    axios
      .post(axiosPath('api_r_user_module_documentManager_tag_list'), {
        moduleBridgeHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change('tags', data, true));
        }
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => dispatch(currentPageAction.change('loading', false, true)));

  useEffect(() => {
    if (loading && permissionsLevel !== undefined) loadTags();
  }, [loading, permissionsLevel]);
  // < LOAD TAGS

  const handleEdit = useCallback(
    async (values) =>
      axios
        .post(axiosPath('api_r_user_module_documentManager_tag_edit'), {
          moduleBridgeHex,
          tagID: showEditModal?.id,
          ...values,
        })
        .then(() => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('loading', true, true));
            setShowEditModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, showEditModal],
  );

  const handleDelete = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_documentManager_tag_delete'), {
          moduleBridgeHex,
          tagID: showDeleteModal,
        })
        .then(() => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('loading', true, true));
            setShowDeleteModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, showDeleteModal],
  );

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath('api_r_user_module_documentManager_getModuleData'),
        'moduleData',
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_documentManager_getPermissions'),
          'permissions',
          {
            moduleBridgeHex,
            subType: 'permissions',
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_documentManager_getPermissions'),
          'userBridge',
          {
            moduleBridgeHex,
            subType: 'userBridge',
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);
  // < STORE CALLS

  return (
    <>
      <DocumentManagerSidebar activeRoute="user_module_documentManager_tag_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `Repositorio ${
                    permissionsLevel === 0 ? '(ADMINISTRADOR)' : ''
                  }`,
                  route: {
                    name: 'user_module_documentManager_item_list',
                    params: {
                      moduleBridgeHex,
                      parentItemHex: '0',
                      page: '1',
                      filtersHex: '0',
                    },
                  },
                },
                {
                  name: 'Palabras clave',
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <AppCard header="Palabras clave">
            <div className="mt-3">
              {loading ? (
                <AppNoInfo iconSize={64} minHeight={window.innerHeight - 400} />
              ) : tags?.length > 0 ? (
                <>
                  <TableHeader>
                    <div>Nombre</div>
                    <div>Color</div>
                    <div />
                  </TableHeader>
                  {tags?.map((t) => (
                    <React.Fragment key={t?.id}>
                      <Container className="p-2" $color={t?.color}>
                        <div className="col-title">
                          <span>{t?.name}</span>
                        </div>
                        <div>
                          <div className="col-color" />
                        </div>
                        <div className="col-options">
                          <div className="options">
                            <span className="pointer pl-2 pr-2" title="Editar">
                              <AppRemixIcon
                                icon="edit-box"
                                color={colors.gray700}
                                onClick={() => setShowEditModal(t)}
                              />
                            </span>
                            <span
                              className="pointer pl-2 pr-2"
                              title="Eliminar"
                              onClick={() => setShowDeleteModal(t?.id)}
                            >
                              <AppRemixIcon
                                icon="delete-bin-6"
                                color={colors.danger}
                              />
                            </span>
                          </div>
                        </div>
                      </Container>
                    </React.Fragment>
                  ))}
                  <AppModal
                    show={!!showEditModal}
                    title="Editar palabra clave"
                    onClose={() => setShowEditModal(false)}
                  >
                    <Formik
                      initialValues={{
                        name: showEditModal?.name,
                        color: showEditModal?.color,
                      }}
                      validationSchema={validationSchema}
                      validateOnChange={false}
                      validateOnBlur={false}
                      onSubmit={handleEdit}
                    >
                      {({
                        handleSubmit,
                        values,
                        setFieldValue,
                        errors,
                        isSubmitting,
                      }) => (
                        <>
                          <div className="row">
                            <div className="col-lg-12 d-flex align-items-center">
                              <AppInput
                                containerClassName="mr-2 flex-grow-1"
                                title="Nombre"
                                value={values.name}
                                error={errors.name}
                                onChange={(opt) => setFieldValue('name', opt)}
                                required
                              />
                              <ColorPicker
                                bgColor={values?.color}
                                handleChange={(v) => setFieldValue('color', v)}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-lg-12 mt-4">
                              <button
                                type="submit"
                                className="btn btn-success float-right"
                                disabled={isSubmitting}
                                onClick={handleSubmit}
                              >
                                Editar
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </Formik>
                  </AppModal>
                  <AppDeleteModal
                    show={showDeleteModal}
                    title="Eliminar palabra clave"
                    onClose={() => setShowDeleteModal(undefined)}
                    onConfirm={handleDelete}
                    text="¿Seguro que desea eliminar la palabra seleccionada? Todas las asignaciones de esta palabra clave a las publicaciones serán eliminadas"
                  />
                </>
              ) : (
                <div className="alert alert-info">No hay palabras clave</div>
              )}
            </div>
          </AppCard>
        </div>
      </main>
    </>
  );
}

const Container = styled.div`
  border-bottom: solid 1px lightgrey;
  overflow: auto;
  display: grid;
  grid-template-columns: 1fr 1fr 300px;

  .col-title {
    cursor: default;
  }
  .col-color {
    width: 20px;
    height: 20px;
    background-color: ${(props) =>
      colors.paletteColors[props.$color]}; // props.$color
    border-radius: 4px;
  }
  .col-options {
    display: flex;
    justify-content: end;
    .options {
      display: none;
    }
  }

  &:hover {
    .options {
      display: flex;
    }
  }
`;

const TableHeader = styled.div`
  position: -webkit-sticky;
  position: sticky;
  top: 40px;
  overflow: auto;

  border-bottom: solid 1px lightgrey;
  display: grid;
  grid-template-columns: 1fr 1fr 300px;
  padding-top: 8px;
  padding-bottom: 8px;

  div {
    font-weight: bold;
  }
`;
